import { useMemo } from 'react';
import { ExtrudeGeometry } from 'three';
import { floorPlankProfile, squareTubeProfile } from './crossSections';
import { SCALE_IN_TO_UNIT, type PlatformPlacement } from '../types';
import { MATERIALS } from '../materials';
import { loadTextureCached, TEXTURE_URLS } from '../materials/textures';
import { useSceneSelection } from '../selection/useSceneSelection';

interface Props {
  placement: PlatformPlacement;
  floor: 'plein' | 'mailles';
  partNumber?: string;
}

export function PlatformDeck({ placement, floor, partNumber }: Props) {
  const widthIn = placement.widthIn;
  const depthIn = placement.depthIn;

  const FRAME_VISUAL_SCALE = 2.5; // frame tubes cross-section ×2.5 for visibility at camera distance

  const frameGeoLong = useMemo(() => {
    const g = new ExtrudeGeometry(squareTubeProfile(), { depth: widthIn, bevelEnabled: false });
    g.translate(0, 0, -widthIn / 2);
    g.scale(
      SCALE_IN_TO_UNIT * FRAME_VISUAL_SCALE,
      SCALE_IN_TO_UNIT * FRAME_VISUAL_SCALE,
      SCALE_IN_TO_UNIT,
    ); // visual scale on cross-section only
    return g;
  }, [widthIn]);

  const frameGeoShort = useMemo(() => {
    const g = new ExtrudeGeometry(squareTubeProfile(), { depth: depthIn, bevelEnabled: false });
    g.translate(0, 0, -depthIn / 2);
    g.scale(
      SCALE_IN_TO_UNIT * FRAME_VISUAL_SCALE,
      SCALE_IN_TO_UNIT * FRAME_VISUAL_SCALE,
      SCALE_IN_TO_UNIT,
    ); // visual scale on cross-section only
    return g;
  }, [depthIn]);

  const plankGeo = useMemo(() => {
    const g = new ExtrudeGeometry(floorPlankProfile(), { depth: depthIn, bevelEnabled: false });
    g.translate(0, 0, -depthIn / 2);
    g.scale(SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT);
    // ADJ-03: shift geometry DOWN by plank height (2") so that the TOP of the
    // deck surface sits exactly at position.y (= heightIn for head platform,
    // yIn for rest landings / turn landings).
    // Without this, the BOTTOM of the profile was at position.y and the top was
    // at position.y + 2", placing the walking surface 2" above the expected level.
    g.translate(0, -2 * SCALE_IN_TO_UNIT, 0);
    return g;
  }, [depthIn]);

  const maillesMaterial = useMemo(() => {
    const alpha = loadTextureCached(TEXTURE_URLS.perforationMailles, depthIn / 6, 1);
    const m = MATERIALS.aluminumPerforated.clone();
    m.alphaMap = alpha;
    m.transparent = true;
    m.normalMap = alpha;
    m.normalScale.set(0.2, 0.2);
    return m;
  }, [depthIn]);

  const halfW = (widthIn / 2) * SCALE_IN_TO_UNIT;
  const halfD = (depthIn / 2) * SCALE_IN_TO_UNIT;
  const frameH = -0.625 * SCALE_IN_TO_UNIT;
  const plankMaterial = floor === 'plein' ? MATERIALS.aluminumBrushed : maillesMaterial;
  const inferredPart = partNumber ?? 'F01005';
  const plankCount = Math.max(1, Math.round(widthIn / 6));
  const plankOff0 = -((plankCount - 1) * 6) / 2;

  return (
    <group
      position={[placement.position.x, placement.position.y, placement.position.z]}
      userData={{
        partNumber: inferredPart,
        kind: 'platform',
        widthIn,
        depthIn,
        refId: placement.id,
      }}
    >
      {/* Long frame rails (front + back) */}
      <mesh
        geometry={frameGeoLong}
        material={MATERIALS.aluminumBrushed}
        position={[0, frameH, halfD]}
        rotation={[0, Math.PI / 2, 0]}
        castShadow
      />
      <mesh
        geometry={frameGeoLong}
        material={MATERIALS.aluminumBrushed}
        position={[0, frameH, -halfD]}
        rotation={[0, Math.PI / 2, 0]}
        castShadow
      />
      {/* Short frame rails (left + right) */}
      <mesh
        geometry={frameGeoShort}
        material={MATERIALS.aluminumBrushed}
        position={[halfW, frameH, 0]}
        castShadow
      />
      <mesh
        geometry={frameGeoShort}
        material={MATERIALS.aluminumBrushed}
        position={[-halfW, frameH, 0]}
        castShadow
      />
      {/* Deck planks */}
      {Array.from({ length: plankCount }).map((_, i) => (
        <mesh
          key={i}
          geometry={plankGeo}
          material={plankMaterial}
          position={[(plankOff0 + i * 6) * SCALE_IN_TO_UNIT, 0, 0]}
          castShadow
          receiveShadow
          userData={{
            partNumber: inferredPart,
            kind: 'platform-plank',
            refId: `${placement.id}-plank-${i}`,
          }}
          onPointerOver={(e) => {
            e.stopPropagation();
            useSceneSelection.getState().setHovered(`${placement.id}-plank-${i}`);
          }}
          onPointerOut={(e) => {
            e.stopPropagation();
            useSceneSelection.getState().setHovered(null);
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            useSceneSelection.getState().setSelected(`${placement.id}-plank-${i}`);
          }}
        />
      ))}
    </group>
  );
}
